import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationApi, type Notification } from '../../api';
import Icon from '../common/Icon';

const POLL_INTERVAL_MS = 60_000; // refresh unread count once a minute

export default function NotificationBell() {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const nav = useNavigate();

  const refreshCount = useCallback(async () => {
    try {
      const r = await notificationApi.unreadCount();
      setUnread(r.unread);
    } catch { /* ignore */ }
  }, []);

  const openPanel = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const r = await notificationApi.list();
      setNotifs(r.notifications);
      setUnread(r.unread);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refreshCount]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [open]);

  const markRead = async (n: Notification) => {
    if (!n.read_at) {
      await notificationApi.markRead(n.ROWID).catch(() => null);
      setNotifs((xs) => xs.map((x) => x.ROWID === n.ROWID ? { ...x, read_at: new Date().toISOString() } : x));
      setUnread((c) => Math.max(0, c - 1));
    }
    if (n.link) {
      setOpen(false);
      nav(n.link);
    }
  };

  const markAll = async () => {
    await notificationApi.markAllRead().catch(() => null);
    setNotifs((xs) => xs.map((x) => x.read_at ? x : { ...x, read_at: new Date().toISOString() }));
    setUnread(0);
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        title="Notifications"
        onClick={() => (open ? setOpen(false) : openPanel())}
        style={bellBtn}
      >
        <Icon name="bell" size={18} />
        {unread > 0 && (
          <span style={badge}>{unread > 9 ? '9+' : unread}</span>
        )}
      </button>
      {open && (
        <div style={dropdown}>
          <div style={dropdownHeader}>
            <strong>Notifications</strong>
            {unread > 0 && (
              <button onClick={markAll} className="ghost" style={{ padding: '4px 8px', fontSize: 12 }}>
                Mark all read
              </button>
            )}
          </div>
          <div style={list}>
            {loading ? (
              <div className="muted" style={{ padding: 16 }}>Loading…</div>
            ) : notifs.length === 0 ? (
              <div className="muted" style={{ padding: 16 }}>
                No notifications yet. Diary reminders and stage events will appear here.
              </div>
            ) : (
              notifs.map((n) => (
                <button
                  key={String(n.ROWID)}
                  onClick={() => markRead(n)}
                  style={{
                    ...item,
                    background: n.read_at ? 'transparent' : 'var(--color-accent-soft)',
                    cursor: n.link ? 'pointer' : 'default',
                  }}
                >
                  <div style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: n.read_at ? 'transparent' : 'var(--color-accent)',
                    flexShrink: 0, marginTop: 6,
                  }} />
                  <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <div style={{ fontWeight: n.read_at ? 400 : 600 }}>{n.title}</div>
                    {n.body && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{n.body}</div>}
                    <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                      {n.CREATEDTIME ? formatShort(n.CREATEDTIME) : ''}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function formatShort(iso: string) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const mins = Math.round((now.getTime() - d.getTime()) / 60000);
    if (mins < 1)   return 'just now';
    if (mins < 60)  return `${mins}m ago`;
    if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
    return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
  } catch { return iso; }
}

const bellBtn: React.CSSProperties = {
  position: 'relative',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 38, height: 38, borderRadius: 'var(--radius)',
  border: '1px solid var(--color-border)',
  background: 'var(--color-surface)',
  cursor: 'pointer',
};
const badge: React.CSSProperties = {
  position: 'absolute', top: -4, right: -4,
  minWidth: 18, height: 18, padding: '0 5px',
  borderRadius: 999,
  background: 'var(--color-danger)', color: 'white',
  fontSize: 10, fontWeight: 600,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const dropdown: React.CSSProperties = {
  position: 'absolute', right: 0, top: 'calc(100% + 6px)',
  width: 360, maxWidth: 'calc(100vw - 24px)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow)',
  zIndex: 200,
  overflow: 'hidden',
};
const dropdownHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '10px 14px',
  borderBottom: '1px solid var(--color-border)',
  background: 'var(--color-surface-2)',
};
const list: React.CSSProperties = {
  maxHeight: 420, overflowY: 'auto',
};
const item: React.CSSProperties = {
  display: 'flex', gap: 10,
  width: '100%',
  padding: '12px 14px',
  border: 'none',
  borderBottom: '1px solid var(--color-border)',
  background: 'transparent',
  fontSize: 13,
  borderRadius: 0,
};
